import type { Notification } from '@/shell/api/queries'
import type { WorkItem } from '@/shell/api/types'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { LinkBroken01 } from '@untitledui/icons'
import { useState } from 'react'
import { Link, NavLink, useSearchParams } from 'react-router'
import { Button } from '@/components/base/buttons/button'
import { api } from '@/shell/api/client'
import { keys, useMyWork, useNotifications, useStalledWork } from '@/shell/api/queries'
import { AssigneeCell } from '@/shell/components/assignee-cell'
import { WorkItemStatus } from '@/shell/components/blocked-age-chip'
import { Block, BlockError, Body, Empty, Skeleton } from '@/shell/components/page'
import { PageBar } from '@/shell/components/page-bar'
import { StatusPill } from '@/shell/components/status-pill'
import { taskGridCols, TaskTableHeader } from '@/shell/components/task-table'
import { WorkClassChip } from '@/shell/components/work-class-chip'
import { fmtDate, fmtDateTime, isOverdue, todayLocal } from '@/shell/format'
import { useListKeys } from '@/shell/keys/use-list-keys'
import { periodLabel } from '@/shell/period'
import { useShell } from '@/shell/store'
import { cx } from '@/utils/cx'
import { HeldEmailsBlock } from './held-emails'
import { NewWorkItemDialog } from './new-work-item'

const WAITING = new Set(['waiting_on_you', 'review'])

/**
 * PRD-018 §3.5 landing. Waiting on you first, then tasks grouped client → file. Does not follow the
 * period: every row carries its own month tag. `?viewing=` is CFO/Controller only; others see a notice.
 * The counts live in the title line, not in stat cards (audit, anti-patterns): a controller wants the rows.
 *
 * QA-32 (ADR 0030): a "Today" section sits first. It holds the day-bound work: recurring items (cash count,
 * deposit, weekly AP run) due today or overdue, and tasks due today. Ordinary close rows stay out;
 * they keep their period tag and stay in the grouped list below. v0.12 adds a separately labeled Stalled block
 * inside Today for exceptions on the signed-in viewer's scoped files; it does not change recurring stamping.
 */
export function MyWorkPage({ tab = 'work' }: { tab?: 'work' | 'notifications' }) {
  const { bootstrap } = useShell()
  const [params] = useSearchParams()
  const items = useMyWork()
  const stalled = useStalledWork()
  const [addingTask, setAddingTask] = useState(false)
  useListKeys(tab === 'work')
  const names = new Map<string, { cluster: string, file: string | null }>()
  for (const t of bootstrap.tree) {
    names.set(t.cluster.id, { cluster: t.cluster.name, file: null })
    for (const f of t.files) names.set(f.id, { cluster: t.cluster.name, file: f.name })
  }
  const today = todayLocal()
  const open = (items.data ?? []).filter(i => i.status !== 'done')
  const todayItems = open.filter(i => !i.blocked?.stalled && ((i.work_class === 'recurring' && i.due_on !== null && i.due_on <= today) || (i.work_class === 'task' && i.due_on === today)))
  const rest = open.filter(i => !todayItems.includes(i))
  const waiting = rest.filter(i => WAITING.has(i.status) || isOverdue(i.due_on, today))
  const tasks = rest.filter(i => !waiting.includes(i))
  const grouped = groupByClientFile(tasks, names)
  const files = new Set(open.map(i => i.file_id ?? i.cluster_id)).size
  const summary = items.isPending ? '…' : `${todayItems.length} today · ${stalled.isError ? '?' : stalled.data?.length ?? 0} stalled · ${waiting.length} waiting on you · ${tasks.length} open ${tasks.length === 1 ? 'task' : 'tasks'} · ${files} ${files === 1 ? 'file' : 'files'}`
  const viewingOther = params.get('viewing')

  return (
    <>
      <PageBar crumbs={[{ label: 'My work' }]} followsPeriod={false} />
      <div className="flex flex-wrap items-start justify-between gap-3 px-8 pt-6">
        <div className="min-w-[320px]">
          <h1 className="text-xl font-semibold text-primary" data-testid="page-title">My work</h1>
          <p className="mt-0.5 text-sm text-tertiary" data-testid="my-work-summary">{summary}</p>
          {viewingOther && !bootstrap.capabilities.viewingControl ? <p role="status" className="mt-2 text-sm text-tertiary">Viewing another person's work is a CFO and Controller control. This is your own My work.</p> : null}
        </div>
        {/* §3.4 title-row rule (v0.4, notes 52 / 57): My work's one primary is New task — before v0.19 the page had no
            create action at all. A required Where select stands in for the client/file context every other New
            work item entry point already has; assignee defaults to the actor. Reuses the existing dialog
            unmodified (Stream B owns its internals). */}
        <div className="flex items-center gap-2" data-testid="title-actions">
          <Button size="sm" onClick={() => setAddingTask(true)} data-testid="action-new-task">New task</Button>
        </div>
      </div>
      {addingTask ? <NewWorkItemDialog clusterId={null} fileId={null} initialKind="task" onClose={() => setAddingTask(false)} /> : null}
      <div className="px-8">
        <nav aria-label="My work sections" className="mt-4 flex gap-1 border-b border-secondary">
          <NavLink end to="/my-work" className={({ isActive }) => cx('-mb-px border-b-2 px-3 py-2 text-sm font-medium', isActive ? 'border-brand text-brand-secondary' : 'border-transparent text-tertiary hover:text-primary')}>Work</NavLink>
          <NavLink to="/my-work/notifications" className={({ isActive }) => cx('-mb-px flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium', isActive ? 'border-brand text-brand-secondary' : 'border-transparent text-tertiary hover:text-primary')}>
            Notifications
            {bootstrap.counts.notificationsUnread > 0 ? <span className="rounded-full px-1.5 text-xs" style={{ background: 'var(--shell-hot-bg)', color: 'var(--shell-hot-fg)' }} data-testid="notifications-badge">{bootstrap.counts.notificationsUnread}</span> : null}
          </NavLink>
        </nav>
      </div>
      {tab === 'notifications'
        ? <NotificationsTab />
        : (
            <Body>
              <Block title="Today" span={12} testId="my-work-today">
                {items.isPending
                  ? <Skeleton rows={2} />
                  : items.isError
                    ? <BlockError message="Could not load today's items. Check your connection and retry." onRetry={() => items.refetch()} />
                    : todayItems.length === 0
                      ? <Empty>Nothing due today. Recurring items (cash counts, deposits, weekly runs) and tasks due today land here; a missed day stays here as overdue until it is done.</Empty>
                      : <ItemList items={todayItems} names={names} />}
                {stalled.isPending
                  ? <Skeleton rows={1} />
                  : stalled.isError
                    ? <BlockError message="Could not check for stalled work on your files." onRetry={() => stalled.refetch()} />
                    : stalled.data.length > 0
                      ? (
                          <section className="mt-4 border-t border-secondary pt-3" aria-label="Stalled on your files" data-testid="my-work-stalled">
                            <h3 className="mb-2 text-xs font-semibold text-error-primary">{`Stalled on your files · ${stalled.data.length}`}</h3>
                            <p className="mb-2 text-xs text-tertiary">A predecessor is past due. These items may belong to someone else or another period.</p>
                            <ItemList items={stalled.data} names={names} showBlocker />
                          </section>
                        )
                      : null}
              </Block>
              <Block title="Waiting on you" span={12}>
                {items.isPending ? <Skeleton /> : items.isError ? <BlockError message="Could not load your items. Check your connection and retry." onRetry={() => items.refetch()} /> : waiting.length === 0 ? <Empty>Nothing is waiting on you. Items land here when someone hands you one, asks for your review, or a due date passes.</Empty> : <ItemList items={waiting} names={names} />}
              </Block>
              {/* PRD-029 §3.7 / PRD-016 §8.6 acceptance 9: held emails sit between Waiting on you and Tasks. */}
              <HeldEmailsBlock />
              <Block title="Tasks" span={12}>
                {items.isPending
                  ? <Skeleton rows={5} />
                  : items.isError
                    ? <BlockError message="Could not load your tasks. Check your connection and retry." onRetry={() => items.refetch()} />
                    : grouped.length === 0
                      ? (
                          <Empty>
                            No open tasks. Press
                            {' '}
                            <kbd className="rounded border border-secondary px-1">c</kbd>
                            {' '}
                            to add one, or open a file's Close tab.
                          </Empty>
                        )
                      : (
                          <div className="flex flex-col gap-5" data-testid="task-groups">
                            {grouped.map(g => (
                              <div key={g.key}>
                                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-quaternary">
                                  {g.cluster}
                                  {g.file ? <span className="font-normal normal-case text-tertiary">{` · ${g.file}`}</span> : null}
                                </h3>
                                <ItemList items={g.items} names={names} />
                              </div>
                            ))}
                          </div>
                        )}
              </Block>
            </Body>
          )}
    </>
  )
}

const NOTIFICATION_LABEL: Record<string, string> = { assigned_to_you: 'assigned to you', mention_or_reply: 'mentioned you', agent_needs_input: 'needs your input', approval_requested: 'asked for your approval', needs_your_attention: 'needs your attention' }

/**
 * PRD-020 §3.9 / §3.11 (migration 0045; Sean 2026-09-18 22:34, Q19): a connection notice is keyed on the file, not a work
 * item. The row opens the file's Overview, where the connection card carries Reconnect / Sync now; the row's own
 * **Reconnect** action deep-links a CFO to Settings → Connections (the list, Check all, Reconnect all; §3.19) and
 * everyone else to the file, since the Connections page is a CFO surface.
 */
function notificationHref(n: Notification): string {
  return n.work_item_id ? `/w/${n.work_item_id}` : `/c/${n.cluster_id}/f/${n.file_id}`
}
function reconnectHref(n: Notification, isCfo: boolean): string {
  return isCfo ? `/settings/connections?file=${n.file_id}` : notificationHref(n)
}

/** PRD-017 §3.9 in-app inbox: unread first; a row opens the item and is read on open or when the item leaves the raising state. */
export function NotificationsTab() {
  const [all, setAll] = useState(false)
  const q = useNotifications(all)
  const qc = useQueryClient()
  const { bootstrap } = useShell()
  const isCfo = bootstrap.me.role === 'cfo'
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: keys.notifications })
    void qc.invalidateQueries({ queryKey: keys.bootstrap })
  }
  const readOne = useMutation({ mutationFn: (id: string) => api(`/notifications/${id}/read`, { method: 'POST' }), onSuccess: refresh })
  const readAll = useMutation({ mutationFn: () => api('/notifications/read-all', { method: 'POST' }), onSuccess: refresh })
  return (
    <Body>
      <Block
        title={all ? 'All notifications' : 'Unread'}
        span={12}
        footer={(
          <div className="flex items-center justify-between">
            <Button size="sm" color="link-color" className="min-h-6" onClick={() => setAll(a => !a)}>{all ? 'Show unread only' : 'Show all'}</Button>
            {(q.data?.unread ?? 0) > 0 ? <Button size="sm" color="secondary" onClick={() => readAll.mutate()} isLoading={readAll.isPending} data-testid="notifications-read-all">Mark all read</Button> : null}
          </div>
        )}
      >
        {q.isPending
          ? <Skeleton rows={4} />
          : q.isError
            ? <BlockError message="Could not load notifications. Check your connection and retry." onRetry={() => q.refetch()} />
            : q.data.notifications.length === 0
              ? <Empty>{all ? 'No notifications yet. Assignments, mentions, agent questions and QuickBooks connection notices land here.' : 'Nothing unread. Assignments, mentions, agent questions and QuickBooks connection notices land here.'}</Empty>
              : (
                  <ul className="divide-y divide-secondary" data-testid="notifications">
                    {q.data.notifications.map(n => (
                      <li key={n.id} className="flex items-center gap-2">
                        <Link to={notificationHref(n)} onClick={() => !n.read_at && readOne.mutate(n.id)} className={cx('-mx-2 flex min-w-0 flex-1 items-center gap-4 rounded-md px-2 py-2 text-sm outline-none hover:bg-secondary focus-visible:ring-2 focus-visible:ring-[var(--shell-focus-ring)]', n.read_at && 'opacity-70')} data-testid="notification-row" data-notification-type={n.type}>
                          <span aria-hidden className={cx('size-2 shrink-0 rounded-full', n.read_at ? 'bg-transparent' : 'bg-fg-brand-secondary')} />
                          {n.type === 'needs_your_attention' ? <LinkBroken01 className={cx('size-4 shrink-0', n.resolved_at ? 'text-fg-quaternary' : 'text-fg-warning-primary')} aria-hidden data-testid="notification-attention-icon" /> : null}
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-primary">
                              {n.type === 'needs_your_attention'
                                ? <span className="font-medium">{n.title}</span>
                                : (
                                    <>
                                      <span className="font-medium">{n.actor_name ?? 'Someone'}</span>
                                      {` ${NOTIFICATION_LABEL[n.type] ?? n.type.replace(/_/g, ' ')} · `}
                                      {n.title}
                                    </>
                                  )}
                            </span>
                            <span className="block truncate text-xs text-quaternary">
                              {n.cluster_name}
                              {n.file_name ? ` · ${n.file_name}` : ''}
                              {n.comment_body ? ` · “${n.comment_body.slice(0, 80)}${n.comment_body.length > 80 ? '…' : ''}”` : ''}
                              {n.detail ? ` · ${n.resolved_at ? (n.detail.resolved_reason ?? 'Resolved') : n.detail.body}` : ''}
                            </span>
                          </span>
                          {n.status ? <StatusPill status={n.status} /> : <span className="rounded-md bg-warning-secondary px-1.5 py-0.5 text-xs text-warning-primary" data-testid="notification-attention-pill">{n.resolved_at ? 'resolved' : 'needs attention'}</span>}
                          <span className="w-32 text-right text-xs tabular-nums text-quaternary"><time dateTime={n.raised_at}>{fmtDateTime(n.raised_at)}</time></span>
                        </Link>
                        {n.type === 'needs_your_attention' && !n.resolved_at
                          ? <Button size="sm" color="secondary" href={reconnectHref(n, isCfo)} onClick={() => !n.read_at && readOne.mutate(n.id)} data-testid="notification-reconnect">{n.detail?.kind === 'needs_reconnect' ? 'Reconnect' : 'Sync now'}</Button>
                          : null}
                      </li>
                    ))}
                  </ul>
                )}
      </Block>
    </Body>
  )
}

/** Rows for My work and the client Close tab: title, where, period tag (PRD-018 §3.5), status, assignee, due — a real column grid with a header row so cells line up regardless of title length (Sean 2026-09-12 08:36). */
export function ItemList({ items, names, showBlocker = false }: { items: WorkItem[], names: Map<string, { cluster: string, file: string | null }>, showBlocker?: boolean }) {
  const today = todayLocal()
  return (
    <div data-testid="item-list">
      <TaskTableHeader withLocation />
      <ul className="divide-y divide-secondary">
        {items.map((i) => {
          const where = names.get(i.file_id ?? i.cluster_id)
          const overdue = i.status !== 'done' && isOverdue(i.due_on, today)
          const href = `/w/${i.id}`
          return (
            <li key={i.id}>
              <Link
                to={href}
                className="grid items-center gap-4 rounded-md px-2 py-2 text-sm outline-none hover:bg-secondary focus-visible:ring-2 focus-visible:ring-[var(--shell-focus-ring)]"
                style={{ gridTemplateColumns: taskGridCols(true) }}
                data-testid="work-item-row"
                data-status={i.status}
                data-list-row
                data-item-href={href}
                data-can-complete={i.status !== 'done' && (i.status === 'not_started' || i.status === 'waiting_on_you' || i.status === 'open')}
                data-can-assign="true"
              >
                <span className="min-w-0">
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="min-w-0 truncate text-primary" data-testid="row-title">{i.title}</span>
                    <WorkClassChip workClass={i.work_class} />
                  </span>
                  {showBlocker && i.blocked?.waitingOn
                    ? <span className="block text-xs text-error-primary">{`Stalled: waiting on ${i.blocked.waitingOn.title} · ${i.blocked.waitingOn.assigneeName ?? 'Owner unavailable'}`}</span>
                    : null}
                </span>
                <span className="truncate text-xs text-quaternary">{where ? `${where.cluster}${where.file ? ` · ${where.file}` : ''}` : ''}</span>
                <span>{i.period ? <span className="rounded bg-secondary px-1.5 py-0.5 text-[11px] font-medium text-tertiary" data-testid="row-period">{periodLabel(i.period)}</span> : null}</span>
                <span><WorkItemStatus status={i.status} blocked={i.blocked} assigneeKind={i.assignee_kind} /></span>
                <AssigneeCell item={i} />
                <span className={cx('text-right text-xs tabular-nums', overdue ? 'font-medium text-error-primary' : 'text-quaternary')}>
                  {i.due_on ? <time dateTime={i.due_on}>{fmtDate(i.due_on)}</time> : ''}
                  {overdue ? <span className="sr-only"> overdue</span> : null}
                </span>
              </Link>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function groupByClientFile(items: WorkItem[], names: Map<string, { cluster: string, file: string | null }>) {
  const groups = new Map<string, { key: string, cluster: string, file: string | null, items: WorkItem[] }>()
  for (const i of items) {
    const key = `${i.cluster_id}:${i.file_id ?? ''}`
    const n = names.get(i.file_id ?? i.cluster_id)
    let g = groups.get(key)
    if (!g) {
      g = { key, cluster: n?.cluster ?? 'Client', file: i.file_id ? n?.file ?? 'File' : null, items: [] }
      groups.set(key, g)
    }
    g.items.push(i)
  }
  return [...groups.values()].sort((a, b) => a.cluster.localeCompare(b.cluster) || (a.file ?? '').localeCompare(b.file ?? ''))
}
